import FlipLeadsAPI from '../../../api/flipLeads';
import types from '../../mutation-types';

// Timeout NAO e lista vazia. O servidor responde 200 com `meta.status === 'timeout'` e payload
// vazio; tratar isso como "nenhum resultado" faria a tela sugerir AMPLIAR a busca, que e o
// conselho oposto do certo — quem estourou o tempo precisa RESTRINGIR.
const isTimeout = meta => meta?.status === 'timeout';

// A RESPOSTA VELHA NAO PODE VENCER A NOVA (#1192). Trocar de filtro enquanto uma busca esta no ar
// deixava a tela com o resultado do filtro ANTERIOR — quem responde por ultimo escrevia na store,
// e quem responde por ultimo pode ser quem saiu primeiro. Numa busca nacional por CNAE a janela e
// de ate 9,7s (#1193), entao ultrapassar a propria requisicao deixou de ser caso raro.
//
// SAO DUAS CAMADAS, e nenhuma substitui a outra:
//
// 1. O NUMERO DE SEQUENCIA decide QUEM ESCREVE. E ele, e nao o cancelamento, que garante a tela
//    correta: uma resposta ja em transito no momento do `abort()` chega assim mesmo (o navegador
//    so descarta o que ainda nao entregou), e um `AbortController` ausente ou falho deixaria a
//    corrida de pe. Sem sequencia, nada garante ordem.
// 2. O `AbortController` devolve TRABALHO ao banco. Com consultas de segundos, deixar a antiga
//    terminar e segurar um worker web e uma varredura de 34 GB para um resultado que ninguem vai
//    ler. Ele nao e refinamento do item 1: um resolve o que a tela mostra, o outro o que o
//    servidor gasta.
//
// O `isFetching` acompanha a requisicao VIGENTE e nao a ultima que terminou — por isso o
// `finally` so o desliga quando a sequencia ainda e a sua. Com o `false` incondicional de antes,
// o indicador apagava no instante em que a busca superada morria, enquanto a boa ainda corria.
//
// Os contadores sao de MODULO porque a store e singleton: dois componentes que despachem `search`
// disputam a mesma tela, e e exatamente essa disputa que precisa ser arbitrada num lugar so.
// `committedSeq` e a busca que esta NA TELA; `searchSeq`, a ultima disparada. Elas divergem
// enquanto uma busca esta no ar, e e dessa diferenca que o `loadMore` precisa — ver o bloco dele.
//
// `emVoo` guarda os controllers de TUDO que escreve na lista (busca e paginacao). Uma variavel so
// nao dava conta: uma busca nova tem de abortar a paginacao que estiver correndo, mas uma
// paginacao NAO pode abortar a busca que a antecede.
let searchSeq = 0;
let committedSeq = 0;
const emVoo = new Set();
let facetsSeq = 0;
let facetsController = null;

const abrirRequisicao = () => {
  const controller = new AbortController();
  emVoo.add(controller);
  return controller;
};

const encerrarRequisicao = controller => emVoo.delete(controller);

export const actions = {
  search: async ({ commit }, { filters = {} } = {}) => {
    searchSeq += 1;
    const seq = searchSeq;
    // Aborta TUDO que estava escrevendo na lista — busca anterior e paginacao em curso. Todo
    // `abort()` daqui e sempre seguido de uma sequencia maior, entao o erro de cancelamento que
    // ele provoca sempre cai no guard de "superada" la embaixo — nunca vira `status: 'error'`.
    emVoo.forEach(controller => controller.abort());
    emVoo.clear();
    const controller = abrirRequisicao();
    const { signal } = controller;

    commit(types.SET_FLIP_LEADS_UI_FLAG, { isFetching: true });
    commit(types.SET_FLIP_LEADS_FILTERS, filters);
    try {
      const { data } = await FlipLeadsAPI.search({ filters, signal });
      if (seq !== searchSeq) return null;

      committedSeq = seq;
      commit(types.CLEAR_FLIP_LEADS);
      commit(types.SET_FLIP_LEADS_META, data.meta);
      if (!isTimeout(data.meta)) {
        commit(types.SET_FLIP_LEADS, data.payload || []);
      }
      return data.meta;
    } catch (error) {
      // Busca SUPERADA morre em silencio: ela nao pode escrever `status: 'error'` sobre o
      // recorte novo, e nao pode relancar — o `runSearch` da SearchView transformaria o
      // cancelamento que nos mesmos pedimos numa faixa vermelha de erro.
      if (seq !== searchSeq) return null;

      // A busca FALHOU mas terminou, e o que esta na tela e o resultado dela (a faixa de erro).
      // Sem esta linha, `searchSeq !== committedSeq` ficaria verdadeiro para sempre depois de um
      // erro e a paginacao seguiria bloqueada — o guard diz "ha busca NO AR", nao "a ultima
      // busca deu certo".
      committedSeq = seq;
      // Busca que falhou nao pode deixar o recorte anterior na tela: o usuario acabou de mudar
      // o filtro e leria as linhas velhas como resposta ao filtro novo.
      commit(types.CLEAR_FLIP_LEADS);
      commit(types.SET_FLIP_LEADS_META, { status: 'error' });
      throw error;
    } finally {
      encerrarRequisicao(controller);
      if (seq === searchSeq) {
        commit(types.SET_FLIP_LEADS_UI_FLAG, { isFetching: false });
      }
    }
  },

  // Ao contrario de `search`, uma falha aqui PRESERVA o que ja foi carregado — o usuario nao
  // perde as paginas que ja tem porque a quarta falhou.
  //
  // A PAGINACAO ENTRA NA MESMA ARBITRAGEM DA BUSCA (#1192/#1224), e por duas razoes distintas:
  //
  // 1. `cursor` e `appliedFilters` sao de RECORTES DIFERENTES enquanto uma busca esta no ar.
  //    `search` comita `SET_FLIP_LEADS_FILTERS` ANTES do await, entao `appliedFilters` ja e o
  //    filtro NOVO enquanto `meta.nextCursor` ainda e o da lista velha — "carregar mais" durante
  //    uma busca mandava filtro novo com cursor velho, que e uma pergunta que ninguem fez. O
  //    guard e `searchSeq !== committedSeq`: ha busca no ar, a lista na tela ja nao e a vigente,
  //    e nao ha o que paginar ainda.
  // 2. A resposta pode chegar DEPOIS de uma busca nova. Sem o guard de volta, o `APPEND` misturava
  //    a pagina do recorte velho na lista nova e o `SET_FLIP_LEADS_META` sobrescrevia
  //    total/exact/cursor dela.
  //
  // O controller entra no `emVoo` para uma busca nova poder CANCELAR esta pagina — mesmo motivo
  // da busca: com consultas de segundos, deixar terminar e trabalho de banco para um resultado
  // que ninguem vai ler. O contrario nao vale: paginar NAO aborta a busca.
  loadMore: async ({ commit, state: $state }) => {
    const cursor = $state.meta.nextCursor;
    if (!cursor || searchSeq !== committedSeq) return null;

    const seq = searchSeq;
    const controller = abrirRequisicao();

    commit(types.SET_FLIP_LEADS_UI_FLAG, { isFetchingMore: true });
    try {
      const { data } = await FlipLeadsAPI.search({
        filters: $state.appliedFilters,
        cursor,
        signal: controller.signal,
      });
      if (seq !== searchSeq) return null;

      if (isTimeout(data.meta)) {
        commit(types.SET_FLIP_LEADS_META, {
          ...data.meta,
          total: $state.meta.total,
          exact: $state.meta.exact,
        });
        return data.meta;
      }
      commit(types.APPEND_FLIP_LEADS, data.payload || []);
      commit(types.SET_FLIP_LEADS_META, data.meta);
      return data.meta;
    } catch (error) {
      // Pagina SUPERADA morre em silencio, como a busca: relancar faria o `loadMore` da
      // SearchView desenhar erro por cima da busca nova, que esta correndo sem problema.
      if (seq !== searchSeq) return null;

      throw error;
    } finally {
      encerrarRequisicao(controller);
      // SEMPRE, e aqui NAO vale o guard de `seq` que o `isFetching` usa la em cima — a diferenca
      // entre as duas flags e ter ou nao ter SUCESSORA. Toda busca nova religa `isFetching` no
      // comeco dela, entao uma busca superada que deixasse a flag acesa seria corrigida em
      // seguida; `isFetchingMore` tem UM produtor so, este par aqui, e ninguem mais a reseta.
      // Com o guard, quem clica em "Carregar mais" e troca o filtro antes de a pagina voltar
      // deixava a flag em `true` pelo resto da sessao — e `woot-button` faz
      // `:disabled="isDisabled || isLoading"`, entao o botao virava spinner desabilitado e a
      // paginacao morria ate recarregar a pagina.
      //
      // Desligar sem guard e seguro porque duas paginacoes simultaneas nao sao alcancaveis: o
      // unico chamador e o botao da SearchView, que fica desabilitado enquanto carrega. E a
      // pagina superada nao tem spinner proprio para proteger — ela morreu em silencio.
      commit(types.SET_FLIP_LEADS_UI_FLAG, { isFetchingMore: false });
    }
  },

  // As contagens saem do cubo, que so conhece cnae/uf/classe_porte/porte_real. Quem chama manda o
  // segmento e o estado de divida, nunca uf/porte: com uf no filtro, o cubo devolveria a contagem
  // apenas das UFs escolhidas e os numeros da lateral sumiriam a cada clique, em vez de servirem
  // de guia para o proximo. Divida e caso diferente e PRECISA ir — ela nao e eixo de faceta, ela
  // troca a MEDIDA somada, entao as 27 UFs continuam na resposta contando so o lado escolhido.
  // Ver o comentario de `refreshFacets` em SearchView.vue.
  //
  // A MESMA corrida da `search` vale aqui, e o estrago e mais dificil de ver: as contagens dos
  // chips passam a descrever OUTRO recorte, e um numero errado no trilho nao se parece com erro
  // nenhum. Contador e controller proprios porque as duas chamadas sao independentes — cancelar
  // a faceta ao disparar a busca deixaria o trilho sem numeros.
  fetchFacets: async ({ commit }, { filters = {} } = {}) => {
    facetsSeq += 1;
    const seq = facetsSeq;
    if (facetsController) facetsController.abort();
    facetsController = new AbortController();
    const { signal } = facetsController;

    commit(types.SET_FLIP_LEADS_UI_FLAG, { isFetchingFacets: true });
    try {
      const { data } = await FlipLeadsAPI.facets({ filters, signal });
      if (seq !== facetsSeq) return;

      commit(types.SET_FLIP_LEADS_FACETS, data.payload || {});
    } catch (error) {
      if (seq !== facetsSeq) return;

      // Faceta e apoio: sem ela a lateral perde os numeros e continua filtrando.
      commit(types.SET_FLIP_LEADS_FACETS, {});
    } finally {
      if (seq === facetsSeq) {
        commit(types.SET_FLIP_LEADS_UI_FLAG, { isFetchingFacets: false });
      }
    }
  },

  fetchCredits: async ({ commit }) => {
    try {
      const { data } = await FlipLeadsAPI.credits();
      commit(types.SET_FLIP_LEADS_CREDITS, data.payload);
      return data.payload;
    } catch (error) {
      // Saldo desconhecido nao bloqueia revelar — o 402 do servidor e quem decide.
      commit(types.SET_FLIP_LEADS_CREDITS, null);
      return null;
    }
  },

  patchLead: ({ commit }, lead) => {
    commit(types.SET_FLIP_LEAD_ITEM, lead);
  },

  // O recorte aplicado MUDOU SEM UMA BUSCA NOVA (#1224). E um caso so, e ele e real: quando as
  // facetas revelam que a base nao tem o dado por tras de um filtro, a tela larga esse filtro —
  // e para o de CNAE secundario o recorte efetivo continua IDENTICO (a busca ja estava vendo so
  // o principal), entao refazer a busca gastaria uma ida ao servidor para receber a mesma lista.
  //
  // Sem isto, porem, `appliedFilters` ficava com a chave que a tela acabou de largar e
  // `matchesStore` passava a dar falso para sempre: voltar do detalhe refazia a busca e jogava
  // fora tudo que ja tinha sido paginado. Action e nao commit direto do componente — a regra da
  // stack vale aqui como em todo lugar.
  patchFilters: ({ commit }, filters) => {
    commit(types.SET_FLIP_LEADS_FILTERS, filters);
  },

  setCredits: ({ commit }, credits) => {
    commit(types.SET_FLIP_LEADS_CREDITS, credits);
  },
};
